import {
  BaseUser,
  Device,
  Document,
  DocumentVersionApproverStatus,
  Folder,
} from "@models";
import {
  DocumentStatus,
  FunctionalGroup,
  RegulatoryTypeEnum,
  Template,
  TemplateConfig,
} from "@types";
import {
  getLatestDocumentStatus,
  getLatestDocumentVersion,
  getNavigableDocumentLabel,
  getSearchableDocumentTextParts,
  getVisibleNavigableDocuments,
} from "@utils";
import {
  compareAlphabetically,
  sortDevicesByName,
} from "src/utils/sorting";
import { getUserDisplayName } from "src/utils/user";

export type FilterOption<T extends string = string> = {
  label: string;
  value: T;
};

type SearchMenuItem = {
  id: string;
  name: string;
};

type DerivedDocumentSearchResult = {
  id: string;
  latestTiptapVersion: number | null;
  latestVersionId: string;
  templateKey: string | null;
  deviceId: string | null;
  folderId: string | null;
  authorUserId: string | null;
  title: string | null;
  filename: string | null;
  defaultTitle: string | null;
  status: DocumentStatus;
  functionalGroupId: string | null;
  regulatoryType: RegulatoryTypeEnum | null;
  approverUserIds: string[];
  hasPendingApproval: boolean;
  needsApproverAssignment: boolean;
  searchText: string;
};

export type DocumentSearchMenuItem = DerivedDocumentSearchResult &
  SearchMenuItem;

export type ActionFilterValue = "waiting-for-approval" | "needs-approvers";

export type OrgSearchMenuFilterState = {
  isOrgOnlySelected: boolean;
  selectedActionFilters: ActionFilterValue[];
  selectedApproverUserIds: string[];
  selectedAuthorUserIds: string[];
  selectedDeviceIds: string[];
  selectedDocumentTypes: RegulatoryTypeEnum[];
  selectedFunctionalGroupIds: string[];
  selectedStatuses: DocumentStatus[];
};

export const ORG_ONLY_SCOPE_VALUE = "org-only-docs";

export const documentStatusLabels: Record<DocumentStatus, string> = {
  [DocumentStatus.AVAILABLE]: "Available",
  [DocumentStatus.ARCHIVED]: "Archived",
  [DocumentStatus.DRAFT]: "Draft",
  [DocumentStatus.LIVE]: "Live",
  [DocumentStatus.UNAPPROVED]: "Unapproved",
};

export const searchableStatuses: DocumentStatus[] = [
  DocumentStatus.DRAFT,
  DocumentStatus.UNAPPROVED,
  DocumentStatus.LIVE,
];

export const actionOptions: FilterOption<ActionFilterValue>[] = [
  {
    label: "Waiting for approval",
    value: "waiting-for-approval",
  },
  {
    label: "Needs approvers",
    value: "needs-approvers",
  },
];

const isReadyForApprovalWithoutRevision = (
  latestVersion: Document["versions"][number] | undefined
) => latestVersion?.readyForApproval === true && latestVersion.revision == null;

const getHasPendingApproval = (
  latestVersion: Document["versions"][number] | undefined
) => {
  if (
    latestVersion == null ||
    !isReadyForApprovalWithoutRevision(latestVersion)
  ) {
    return false;
  }

  return latestVersion.documentVersionApprover.some(
    (approver) =>
      approver.approvalStatus !== DocumentVersionApproverStatus.APPROVED
  );
};

const getNeedsApproverAssignment = (
  latestVersion: Document["versions"][number] | undefined
) => {
  if (
    latestVersion == null ||
    !isReadyForApprovalWithoutRevision(latestVersion)
  ) {
    return false;
  }

  return latestVersion.documentVersionApprover.length < 2;
};

export const matchesSelectedActionFilter = (
  item: DocumentSearchMenuItem,
  actionFilter: ActionFilterValue
) => {
  switch (actionFilter) {
    case "waiting-for-approval":
      return item.hasPendingApproval;
    case "needs-approvers":
      return item.needsApproverAssignment;
    default:
      return false;
  }
};

export type DocumentTemplateResolver = (
  document: Document
) => Template | undefined;

export const getTemplateForDocument = ({
  document,
  resolveTemplateForDocument,
  templatesConfig,
}: {
  document: Document;
  resolveTemplateForDocument?: DocumentTemplateResolver;
  templatesConfig: TemplateConfig | undefined;
}) =>
  resolveTemplateForDocument?.(document) ??
  (document.templateKey ? templatesConfig?.[document.templateKey] : undefined);

export const toggleSelection = <T extends string>(current: T[], value: T) =>
  current.includes(value)
    ? current.filter((currentValue) => currentValue !== value)
    : [...current, value];

export const getRegulatoryTypeLabel = (type: RegulatoryTypeEnum) => type;

export const getDocumentDeviceIds = (documents: Document[]) =>
  [
    ...new Set(
      documents
        .map((document) => document.deviceId)
        .filter((deviceId): deviceId is string => Boolean(deviceId))
    ),
  ].sort(compareAlphabetically);

export const getSortedDeviceOptions = (devices: Device[]): FilterOption[] =>
  sortDevicesByName(devices)
    .map((device) => ({
      value: device.id,
      label: device.name,
    }));

export const getSelectedUsers = (
  selectedUserIds: string[],
  users: BaseUser[]
) =>
  selectedUserIds
    .map((userId) => users.find((user) => user.id === userId))
    .filter((user): user is BaseUser => user != null);

export const buildDocumentSearchItems = ({
  documents,
  functionalGroups,
  resolveTemplateForDocument,
  templatesConfig,
}: {
  documents: Document[];
  functionalGroups?: FunctionalGroup[];
  resolveTemplateForDocument?: DocumentTemplateResolver;
  templatesConfig: TemplateConfig | undefined;
}): DocumentSearchMenuItem[] => {
  const functionalGroupIdsByAcronym = new Map(
    (functionalGroups ?? []).map((group) => [group.acronym, group.id])
  );

  return getVisibleNavigableDocuments({
    documents,
    resolveTemplateForDocument,
    templatesConfig,
  })
    .flatMap((document) => {
      const latestVersion = getLatestDocumentVersion(document);
      const name = getNavigableDocumentLabel(document);

      if (!latestVersion) {
        return [];
      }

      const template = getTemplateForDocument({
        document,
        resolveTemplateForDocument,
        templatesConfig,
      });

      if (template?.hidden || !name) {
        return [];
      }

      return [
        {
          id: document.id,
          latestTiptapVersion: latestVersion.tiptapVersion ?? null,
          latestVersionId: latestVersion.id,
          templateKey: document.templateKey,
          deviceId: document.deviceId ?? null,
          folderId: document.folderId ?? null,
          authorUserId: latestVersion.createdBy || null,
          name,
          title: document.title ?? null,
          filename: latestVersion.file?.filename ?? null,
          defaultTitle: template?.defaultTitle ?? null,
          status: getLatestDocumentStatus(document),
          functionalGroupId:
            document.functionalGroupId ??
            (template?.functionalGroupAcronym
              ? functionalGroupIdsByAcronym.get(
                  template.functionalGroupAcronym
                ) ?? null
              : null),
          regulatoryType:
            document.regulatoryType ?? template?.regulatoryType ?? null,
          approverUserIds: latestVersion.documentVersionApprover
            .map((approver: { userId?: string | null }) => approver.userId)
            .filter((userId: string | null | undefined): userId is string =>
              Boolean(userId)
            ),
          hasPendingApproval: getHasPendingApproval(latestVersion),
          needsApproverAssignment: getNeedsApproverAssignment(latestVersion),
          searchText: getSearchableDocumentTextParts(document, {
            resolveTemplateForDocument,
            templatesConfig,
          }).join(" "),
        },
      ];
    })
    .slice()
    .sort((left, right) => {
      const byName = compareAlphabetically(left.name, right.name);

      if (byName !== 0) {
        return byName;
      }

      return compareAlphabetically(left.id, right.id);
    });
};

export const getFunctionalGroupOptions = (
  functionalGroups: FunctionalGroup[] | undefined
): FilterOption<string>[] =>
  (functionalGroups ?? [])
    .map((fg) => ({
      value: fg.id,
      label: `${fg.name} (${fg.acronym})`,
    }))
    .sort((left, right) => compareAlphabetically(left.label, right.label));

export const getDocumentTypeOptions = (
  items: DocumentSearchMenuItem[]
): FilterOption<RegulatoryTypeEnum>[] =>
  [...new Set(items.map((item) => item.regulatoryType))]
    .filter((value): value is RegulatoryTypeEnum => value != null)
    .map((value) => ({
      value,
      label: getRegulatoryTypeLabel(value),
    }))
    .sort((left, right) => compareAlphabetically(left.label, right.label));

export const getStatusOptions = (): FilterOption<DocumentStatus>[] =>
  searchableStatuses.map((status) => ({
    value: status,
    label: documentStatusLabels[status],
  }));

export const filterDocumentSearchItems = (
  items: DocumentSearchMenuItem[],
  filters: OrgSearchMenuFilterState
) => {
  const hasScopedLocationFilter =
    filters.selectedDeviceIds.length > 0 || filters.isOrgOnlySelected;

  return items.filter((item) => {
    if (hasScopedLocationFilter) {
      const matchesOrgOnly = filters.isOrgOnlySelected && item.deviceId == null;
      const matchesDevice =
        item.deviceId != null &&
        filters.selectedDeviceIds.includes(item.deviceId);

      if (!matchesOrgOnly && !matchesDevice) {
        return false;
      }
    }

    if (
      filters.selectedStatuses.length > 0 &&
      !filters.selectedStatuses.includes(item.status)
    ) {
      return false;
    }

    if (
      filters.selectedFunctionalGroupIds.length > 0 &&
      (item.functionalGroupId == null ||
        !filters.selectedFunctionalGroupIds.includes(item.functionalGroupId))
    ) {
      return false;
    }

    if (
      filters.selectedDocumentTypes.length > 0 &&
      (item.regulatoryType == null ||
        !filters.selectedDocumentTypes.includes(item.regulatoryType))
    ) {
      return false;
    }

    if (
      filters.selectedAuthorUserIds.length > 0 &&
      (item.authorUserId == null ||
        !filters.selectedAuthorUserIds.includes(item.authorUserId))
    ) {
      return false;
    }

    if (
      filters.selectedApproverUserIds.length > 0 &&
      !item.approverUserIds.some((userId) =>
        filters.selectedApproverUserIds.includes(userId)
      )
    ) {
      return false;
    }

    if (
      filters.selectedActionFilters.length > 0 &&
      !filters.selectedActionFilters.some((actionFilter) =>
        matchesSelectedActionFilter(item, actionFilter)
      )
    ) {
      return false;
    }

    return true;
  });
};

export const hasActiveFilters = (filters: OrgSearchMenuFilterState) =>
  filters.isOrgOnlySelected ||
  filters.selectedDeviceIds.length > 0 ||
  filters.selectedStatuses.length > 0 ||
  filters.selectedFunctionalGroupIds.length > 0 ||
  filters.selectedDocumentTypes.length > 0 ||
  filters.selectedAuthorUserIds.length > 0 ||
  filters.selectedApproverUserIds.length > 0 ||
  filters.selectedActionFilters.length > 0;

export const getDeviceNamesById = (devices: Device[]) =>
  new Map(devices.map((device) => [device.id, device.name]));

export const getFolderNamesById = (folders: Folder[]) =>
  new Map(folders.map((folder) => [folder.id, folder.name]));

export const getUserNamesById = (users: BaseUser[]) =>
  new Map(users.map((user) => [user.id, getUserDisplayName(user)]));

export const getFunctionalGroupNamesById = (
  functionalGroups: FunctionalGroup[] | undefined
) =>
  new Map((functionalGroups ?? []).map((fg) => [fg.id, fg.name]));

export const getDocumentSearchText = (
  item: DocumentSearchMenuItem,
  {
    deviceNamesById,
    folderNamesById,
    functionalGroupNamesById,
    userNamesById,
  }: {
    deviceNamesById: Map<string, string>;
    folderNamesById: Map<string, string>;
    functionalGroupNamesById: Map<string, string>;
    userNamesById: Map<string, string>;
  }
) =>
  [
    item.name,
    item.title,
    item.filename,
    item.defaultTitle,
    item.templateKey,
    item.searchText,
    item.deviceId ? deviceNamesById.get(item.deviceId) : "Org document",
    item.folderId ? folderNamesById.get(item.folderId) : null,
    item.functionalGroupId
      ? functionalGroupNamesById.get(item.functionalGroupId)
      : null,
    item.regulatoryType ? getRegulatoryTypeLabel(item.regulatoryType) : null,
    documentStatusLabels[item.status],
    item.authorUserId ? userNamesById.get(item.authorUserId) : null,
    ...item.approverUserIds
      .map((userId) => userNamesById.get(userId))
      .filter((value): value is string => Boolean(value)),
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ");
